import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { MapPin, Zap, Shield, MessageCircle, Star, CreditCard } from "lucide-react";

export default function Landing() {
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 bg-background/80 backdrop-blur border-b border-border">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary grid place-items-center text-primary-foreground font-bold bb-display">B</div>
            <span className="font-semibold bb-display">BallBoy</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login"><Button variant="ghost" size="sm" data-testid="landing-login-button">Log in</Button></Link>
            <Link to="/signup"><Button size="sm" data-testid="landing-signup-button">Get started</Button></Link>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 py-12 sm:py-20">
        <section className="grid md:grid-cols-2 gap-8 items-center">
          <div>
            <p className="text-primary font-medium text-sm mb-3">Your personal practice assistant</p>
            <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight bb-display">
              Book a ball boy for your next session.
            </h1>
            <p className="mt-4 text-muted-foreground max-w-prose">
              Athletes request on‑demand practice helpers nearby. Verified ball boys get notified, accept jobs, chat live, and get paid — all in one beautiful app.
            </p>
            <div className="mt-6 flex gap-3">
              <Link to="/signup"><Button size="lg" data-testid="landing-cta-get-started">Get started</Button></Link>
              <Link to="/login"><Button size="lg" variant="secondary">I have an account</Button></Link>
            </div>
          </div>
          <div className="relative">
            <img
              alt="Courtside"
              src="https://images.pexels.com/photos/9899398/pexels-photo-9899398.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              className="rounded-3xl w-full h-72 object-cover border border-border shadow-[var(--bb-shadow)]"
            />
            <div className="absolute -bottom-6 -left-4 bg-card border border-border rounded-2xl shadow-[var(--bb-shadow)] p-4 w-60">
              <div className="text-xs text-muted-foreground">ETA</div>
              <div className="text-3xl bb-display font-semibold tabular-nums">6 min</div>
              <div className="text-xs text-[hsl(var(--success))]">Ball boy en route</div>
            </div>
          </div>
        </section>
        <section className="mt-24 grid sm:grid-cols-2 md:grid-cols-3 gap-4">
          {FEATURES.map((f, i) => (
            <div key={i} className="rounded-2xl border border-border bg-card p-5">
              <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary grid place-items-center mb-3">
                <f.icon className="h-5 w-5" />
              </div>
              <div className="font-semibold bb-display">{f.title}</div>
              <p className="text-sm text-muted-foreground mt-1">{f.body}</p>
            </div>
          ))}
        </section>
      </main>
      <footer className="border-t border-border py-6 text-center text-xs text-muted-foreground">
        © BallBoy · <Link to="/terms" className="underline">Terms</Link> · <Link to="/privacy" className="underline">Privacy</Link>
      </footer>
    </div>
  );
}

const FEATURES = [
  { icon: MapPin, title: "Geolocation matching", body: "Real-time nearby ball boys based on your session location." },
  { icon: Shield, title: "Verified ball boys", body: "Every ball boy uploads ID and is admin-approved before accepting jobs." },
  { icon: Zap, title: "Live ETA & status", body: "Accepted → en route → arrived → complete, with live notifications." },
  { icon: MessageCircle, title: "In-app chat", body: "Coordinate with your match in real time." },
  { icon: CreditCard, title: "PayPal payments", body: "Secure checkout, fees recorded, payouts tracked for ball boys." },
  { icon: Star, title: "Two-way ratings", body: "Rate after every session to keep the community excellent." },
];
